import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Grid } from '@ltvco/refresh-lib/theme';
import { searchFormsRecyclingValidation } from '@ltvco/refresh-lib/utils';
import { SearchReportResponseData, useSession } from '@ltvco/refresh-lib/v1';
import { useForm, useMutation } from '@ltvco/refresh-lib/vendors';
import React, { useCallback, useContext, useState } from 'react';

import { SearchButton } from '../components';
import { SelectInput } from '../SelectInput';
import { SearchProps } from '../utils';

import {
  VehicleSearchFieldContainer,
  VehicleFormContainer,
  IntegratedSearchContainer,
} from './components';
import { LicensePlateSearchInput } from './LicensePlateSearchInput';
import {
  VehicleFormData,
  VehicleSearchType,
  searchTypeOptions,
  getResolver,
  vehicleSearch,
  SearchParams,
  getPlaceholderTextForVehicleSearch,
} from './utils';
import { VINSearchInput } from './VINSearchInput';
import { YMMSearchInput } from './YMMSearchInput';

export const VehicleSearch = React.memo(
  ({
    onSearchSuccess,
    isMobile = false,
    placement = 'dashboard',
  }: SearchProps) => {
    const {
      session: { account },
    } = useSession();
    const { logError, trackEventGA4 } = useContext(AppConfig);
    const [searchType, setSearchType] = useState<VehicleSearchType>(
      VehicleSearchType.VIN
    );
    const [customPlaceholderText, setCustomPlaceholderText] = useState(
      getPlaceholderTextForVehicleSearch(VehicleSearchType.VIN, isMobile)
    );
    const searchFrom = `${placement}_search`;

    const {
      handleSubmit,
      control,
      formState: { errors },
      setValue,
      setError,
      clearErrors,
      watch,
      trigger,
      register,
      reset,
    } = useForm<VehicleFormData>({
      resolver: getResolver(searchType),
      defaultValues: {
        vin: '',
        licensePlate: '',
        state: 'All',
        year: '',
        make: '',
        model: '',
        trim: '',
        searchType: VehicleSearchType.VIN,
      },
    });

    const vehicleQuery = useMutation<
      SearchReportResponseData,
      Error,
      SearchParams
    >({
      mutationFn: vehicleSearch,
      onSuccess: (data) => {
        onSearchSuccess &&
          onSearchSuccess({
            permalink: data.report.permalink,
            reportType: 'vehicle',
            searchParams: '',
          });
      },
      onError: (error) => {
        logError('Vehicle Search', error);

        if (searchType === VehicleSearchType.VIN) {
          setError('vin', {
            message: 'We could not find any information for this VIN',
          });
        } else if (searchType === VehicleSearchType.LICENSE_PLATE) {
          setError('licensePlate', {
            message: 'We could not find any information for this license plate',
          });
        } else if (searchType === VehicleSearchType.YEAR_MAKE_MODEL) {
          if (!yearValue) {
            setError('year', { message: 'Year is required' });
          } else if (!makeValue) {
            setError('make', { message: 'Make is required' });
          } else if (!modelValue) {
            setError('model', { message: 'Model is required' });
          } else if (!trimValue) {
            setError('trim', { message: 'Trim is required' });
          }
        }
      },
    });

    const vinValue = watch('vin');
    const licensePlateValue = watch('licensePlate');
    const stateValue = watch('state');
    const yearValue = watch('year');
    const makeValue = watch('make');
    const modelValue = watch('model');
    const trimValue = watch('trim');

    const handleSearchTypeChange = useCallback(
      (value: string) => {
        const searchTypeValue = value as VehicleSearchType;
        setSearchType(searchTypeValue);
        setCustomPlaceholderText(
          getPlaceholderTextForVehicleSearch(searchTypeValue, isMobile)
        );
        reset({
          vin: '',
          licensePlate: '',
          state: 'All',
          year: '',
          make: '',
          model: '',
          trim: '',
          searchType: searchTypeValue,
        });
        clearErrors();
      },
      [setValue, clearErrors, reset, isMobile]
    );

    const validateYMMFields = () => {
      if (!yearValue) {
        setError('year', { message: 'Year is required' });
        return false;
      }

      if (!makeValue) {
        setError('make', { message: 'Make is required' });
        return false;
      }

      if (!modelValue) {
        setError('model', { message: 'Model is required' });
        return false;
      }

      return true;
    };

    const handleVINSearch = useCallback(
      (data: VehicleFormData) => {
        if (!searchFormsRecyclingValidation(account)) {
          return;
        }

        trackEventGA4({
          event_name: 'report_search',
          report_type: 'vehicle',
          search_by: 'vin',
          search_from: searchFrom,
        });

        vehicleQuery.mutate({ vin: data.vin });
      },
      [account, vehicleQuery, trackEventGA4, searchFrom]
    );

    const handleLicensePlateSearch = useCallback(
      (data: VehicleFormData) => {
        if (!searchFormsRecyclingValidation(account)) {
          return;
        }

        trackEventGA4({
          event_name: 'report_search',
          report_type: 'vehicle',
          search_by: 'license_plate',
          search_from: searchFrom,
        });

        vehicleQuery.mutate({
          licensePlate: data.licensePlate,
          state: data.state,
        });
      },
      [account, vehicleQuery, trackEventGA4, searchFrom]
    );

    const handleYMMSearch = useCallback(
      (data: VehicleFormData) => {
        if (!searchFormsRecyclingValidation(account)) {
          return;
        }

        if (!validateYMMFields()) {
          return;
        }

        trackEventGA4({
          event_name: 'report_search',
          report_type: 'vehicle',
          search_by: 'year_make_model',
          search_from: searchFrom,
        });

        vehicleQuery.mutate({
          year: data.year,
          make: data.make,
          model: data.model,
          trim: data.trim || '',
        });
      },
      [account, vehicleQuery, trackEventGA4, searchFrom]
    );

    const onVehicleSearchSubmit = (data: VehicleFormData) => {
      switch (searchType) {
        case VehicleSearchType.VIN:
          handleVINSearch(data);
          break;
        case VehicleSearchType.LICENSE_PLATE:
          handleLicensePlateSearch(data);
          break;
        case VehicleSearchType.YEAR_MAKE_MODEL:
          handleYMMSearch(data);
          break;
      }
    };

    const renderSearchInput = () => {
      switch (searchType) {
        case VehicleSearchType.VIN:
          return (
            <VINSearchInput
              placeholderText={customPlaceholderText}
              register={register}
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              control={control}
              trigger={trigger}
              value={vinValue}
              onSubmit={handleSubmit(onVehicleSearchSubmit)}
              isMobile={isMobile}
            />
          );
        case VehicleSearchType.LICENSE_PLATE:
          return (
            <LicensePlateSearchInput
              placeholderText={customPlaceholderText}
              register={register}
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              control={control}
              trigger={trigger}
              value={licensePlateValue}
              stateValue={stateValue}
              onSubmit={handleSubmit(onVehicleSearchSubmit)}
              isMobile={isMobile}
            />
          );
        case VehicleSearchType.YEAR_MAKE_MODEL:
          return (
            <YMMSearchInput
              register={register}
              errors={errors}
              clearErrors={clearErrors}
              setValue={setValue}
              control={control}
              trigger={trigger}
              yearValue={yearValue}
              makeValue={makeValue}
              modelValue={modelValue}
              trimValue={trimValue}
              onSubmit={handleSubmit(onVehicleSearchSubmit)}
              isMobile={isMobile}
            />
          );
        default:
          return null;
      }
    };

    const renderMobileForm = useCallback(() => {
      return (
        <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
          <VehicleSearchFieldContainer>
            <SelectInput
              options={searchTypeOptions}
              value={searchType}
              onChange={handleSearchTypeChange}
              name="searchType"
              control={control}
            />
          </VehicleSearchFieldContainer>

          <Box flexGrow={1}>{renderSearchInput()}</Box>

          <SearchButton type="submit" fullWidth>
            Search
          </SearchButton>
        </Grid>
      );
    }, [searchType, errors, handleSearchTypeChange, control]);

    const renderDesktopForm = useCallback(() => {
      return (
        <VehicleFormContainer>
          <Box flexGrow={1}>
            <VehicleSearchFieldContainer
              container
              className={Object.keys(errors).length > 0 ? 'error' : ''}
            >
              <Grid
                size={{ xs: 12 }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                }}
              >
                <IntegratedSearchContainer>
                  <Box
                    sx={{
                      '& .MuiFormControl-root': {
                        minWidth: '190px',
                      },
                    }}
                  >
                    <SelectInput
                      options={searchTypeOptions}
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      name="searchType"
                      control={control}
                    />
                  </Box>

                  {renderSearchInput()}
                </IntegratedSearchContainer>
              </Grid>
            </VehicleSearchFieldContainer>
          </Box>
          <SearchButton type="submit">Search</SearchButton>
        </VehicleFormContainer>
      );
    }, [searchType, errors, handleSearchTypeChange, control]);

    return (
      <form onSubmit={handleSubmit(onVehicleSearchSubmit)}>
        {isMobile ? renderMobileForm() : renderDesktopForm()}
      </form>
    );
  }
);
